import { ReactNode, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownMenuSection from "ds/components/DropdownMenu/Section";
import DropdownMenuDivider from "ds/components/DropdownMenu/Divider";
import AssistantPanelDropdownMenu from "components/AssistantPanel/components/DropdownMenu";
import { Dots, Expand, External, Pin } from "components/icons/generated";
import { IconComponent } from "types/Icon";
import useDetectMobile from "hooks/useDetectMobile";
import useIsAssistantPageMode from "components/AssistantPanel/hooks/useIsAssistantPageMode";

import useAssistantPanelStore from "../../useStore";
import { AssistantPanelVariant } from "../../types";
import useAssistantPanelAnalytics from "../../../analytics/useAnalytics";

const VARIANT_OPTIONS: { variant: AssistantPanelVariant; label: string; icon: IconComponent }[] = [
  { variant: "floating", label: "Floating", icon: External },
  { variant: "fullscreen", label: "Full screen", icon: Expand },
  { variant: "side", label: "Pinned", icon: Pin },
];

type AssistantPanelLayoutHeaderModeDropdownProps = {
  children?: ReactNode;
  dangerActions?: ReactNode;
};

const AssistantPanelLayoutHeaderModeDropdown = ({
  children,
  dangerActions,
}: AssistantPanelLayoutHeaderModeDropdownProps) => {
  const isMobile = useDetectMobile();
  const isAssistantPageMode = useIsAssistantPageMode();
  const { variant, pin, unpin, toggleFullscreen } = useAssistantPanelStore(
    useShallow((state) => ({
      variant: state.variant,
      pin: state.pin,
      unpin: state.unpin,
      toggleFullscreen: state.toggleFullscreen,
    }))
  );
  const { trackPanelVariantChanged } = useAssistantPanelAnalytics();

  const handleSelect = useCallback(
    (target: AssistantPanelVariant) => {
      if (target === variant) {
        return;
      }
      if (target === "side") {
        pin();
      } else if (target === "floating") {
        unpin();
      } else {
        toggleFullscreen();
      }
      trackPanelVariantChanged({ variant: target });
    },
    [variant, pin, unpin, toggleFullscreen, trackPanelVariantChanged]
  );

  if (isMobile) {
    return undefined;
  }

  return (
    <AssistantPanelDropdownMenu
      placement="bottom right"
      triggerComponent={
        <ButtonIcon icon={Dots} variant="ghost">
          More actions
        </ButtonIcon>
      }
    >
      {!isAssistantPageMode && (
        <DropdownMenuSection>
          {VARIANT_OPTIONS.map(({ variant: optionVariant, label, icon }) => (
            <DropdownMenuItem
              key={optionVariant}
              active={optionVariant === variant}
              onAction={() => handleSelect(optionVariant)}
            >
              <Box gap="medium" align="center">
                <Icon src={icon} />
                {label}
              </Box>
            </DropdownMenuItem>
          ))}
        </DropdownMenuSection>
      )}
      {!isAssistantPageMode && children && <DropdownMenuDivider />}
      {children && <DropdownMenuSection>{children}</DropdownMenuSection>}
      {dangerActions && <DropdownMenuDivider />}
      {dangerActions && <DropdownMenuSection>{dangerActions}</DropdownMenuSection>}
    </AssistantPanelDropdownMenu>
  );
};

export default AssistantPanelLayoutHeaderModeDropdown;
